import { Injectable } from '@angular/core';
import { CommonUtils } from '../../../js/static/CommonUtils';
import { Utils } from '../../../js/Utils';
import { WindowService } from '../window/window.service';

@Injectable()
export class ParserService {
  constructor(protected windowService?: WindowService) {}
  parse(type, value) {
    let propType = type;

    /** Copied from MDN (a more secure way to parse a float than parseFloat) */
    const filterFloat = function (value) {
      if (/^(\-|\+)?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value)) {
        return Number(value);
      }
      return NaN;
    };

    if (value === null) {
      return null;
    }

    // TODO: Use typescript's refelect metadata API to get the type info
    // which is safe
    const tokens = type.toString().match(/function (.*)\(/);
    if (tokens) {
      propType = tokens[1];
    }

    switch (propType) {
      case 'Boolean':
        return value === true || value === 'true' ? true : false;
      case 'Currency':
        return this.parseCurrency(value);
      case 'Date':
        return this.parseUTCDate(value);
      case 'GeoName':
        return this.parseGeoName(value);
      case 'Number':
        const parsedValue: number = filterFloat(value);
        return isNaN(parsedValue) ? null : parsedValue;
      case 'ShortDate':
        return this.parseShortDate(value);
      case 'SSN':
        return this.parseSSN(value);
      case 'TIN':
        return this.parseTIN(value);
      case 'EIN':
        return this.parseEIN(value);
      case 'Telephone':
        return this.parsePhoneNumber(value);
      case 'CurrencyZero':
        return this.parseCurrency(value, true);
      case 'zip':
        return this.parseZipcode(value);
      case 'Name':
        return this.parseName(value);
      case 'Text':
        return this.parseText(value);
      case 'CurrencyRounded':
        return this.parseCurrencyRounded(value);
      case 'CurrencyRoundedConditionally':
        return this.parseCurrencyRounded(value, true);
      case 'CurrencyRoundedToDollar':
        return this.parseCurrencyRoundedToDollar(value);
      default:
        return value;
    }
  }

  // Takes first rule available for parsing... we should actually separate validation rules
  // and parsers.  Ignore 'required'.  The reason why we look at rules first
  // is because there are different ways to parse the same object type (e.g. String - Telephone)
  public deriveParserTypeFromRule(rule: string): string {
    let parserType = null;

    switch (rule) {
      case 'currency':
      case 'currencyWithCents':
        parserType = 'Currency';
        break;
      case 'currencyZero':
        parserType = 'CurrencyZero';
        break;
      case 'geoName':
        parserType = 'GeoName';
        break;
      case 'DOB':
      case 'date':
      case 'futuredate':
        parserType = 'Date';
        break;
      case 'numeric':
      case 'numericInt':
      case 'numericPositive':
        parserType = 'Number';
        break;
      case 'shortdate':
        parserType = 'ShortDate';
        break;
      case 'SSN':
        parserType = 'SSN';
        break;
      case 'EIN':
        parserType = 'EIN';
        break;
      case 'telephone':
        parserType = 'Telephone';
        break;
      case 'zipcode':
        parserType = 'zip';
        break;
      case 'name':
        parserType = 'Name';
        break;
      case 'currencyRounded':
        parserType = 'CurrencyRounded';
      case 'currencyRoundedConditionally':
        parserType = 'CurrencyRoundedConditionally';
      default:
        break;
    }

    return parserType;
  }

  /* Parsers */
  private parseUTCDate(value) {
    if (value === null || value.length === 0) {
      return null;
    }

    const dtParts = value.split('/');
    const parsed = Date.UTC(dtParts[2], Number(dtParts[0]) - 1, dtParts[1]);

    const dateCheck = new Date(value);
    if (isNaN(parsed) || !dateCheck.valueOf()) {
      return null;
    }

    return new Date(parsed);
  }

  private parseShortDate(value) {
    if (value === null || value.length === 0) {
      return null;
    }

    const dtParts = value.split('/');
    if (Number(dtParts[0]) < 1 || Number(dtParts[0]) > 12) {
      return null;
    }
    const parsed = Date.UTC(dtParts[1], Number(dtParts[0]) - 1);

    if (isNaN(parsed)) {
      return null;
    }

    return new Date(parsed);
  }

  private isDateValid(date) {
    const dateRegex = /^([0]?[1-9]|[1][0-2])[.\/-]([0]?[1-9]|[1|2][0-9]|[3][0|1])[.\/-]([0-9]{4}|[0-9]{2})$/;
    return date.match(dateRegex);
  }

  private parsePhoneNumber(value) {
    return CommonUtils.parsePhoneNumber(value);
  }
  private parseName(value) {
    const parsedName = value.replace(/[^a-z ,.'-_]/gi, '');
    return parsedName;
  }
  private parseZipcode(value) {
    const parseZipcode = value.replace(/[^0-9]/g, '');
    return parseZipcode;
  }

  private parseCurrency(value, allowZero?: boolean) {
    if (!value && allowZero) {
      return 0;
    }

    let n = value;

    n = n.replace(/k/i, '000');
    n = n.replace(/[^0-9\.\-]/g, '');

    const f = parseFloat(n);

    if (isNaN(f)) {
      return null;
    }

    const result = Math.floor(f * 100 + 0.5) / 100;

    return result;
  }

  /**
   * Parses the raw digits out of a given SSN input, and returns it formatted appropriately
   */
  private parseSSN(value) {
    const rawValue = value.replace(/[^0-9]/g, '');
    return this.formatSSN(rawValue);
  }

  private parseTIN(value: string) {
    const rawValue = value.replace(/[^0-9]/g, '');
    return this.formatEINorTIN(rawValue);
  }

  private parseEIN(value) {
    const rawValue = value.replace(/[^0-9]/g, '');
    return this.formatEINorTIN(rawValue);
  }

  private formatSSN(value) {
    if (!value || value.length < 9) {
      return '';
    }

    return value.substr(0, 3) + '-' + value.substr(3, 2) + '-' + value.substr(5, 4);
  }

  private formatEINorTIN(value) {
    if (!value || value.length < 9) {
      return '';
    }

    return value.substr(0, 2) + '-' + value.substr(2, 7);
  }
  private parseGeoName(value) {
    return this.parseText(value);
  }

  private parseText(value) {
    if (!value) {
      return '';
    }

    return value.trim();
  }

  private parseCurrencyRounded(value: number, checkForFlag?: boolean): number {
    const dollarIncrementEnabled = this.windowService.nativeWindow.controller.nonroundedFaceAmountsEnabled;
    const n = this.parseCurrency(value);
    if (n === null) {
      return n;
    }

    //coverpath policies enable dollar increments based on feature flag
    return checkForFlag && dollarIncrementEnabled ? n : Utils.roundToNearestThousand(n);
  }

  private parseCurrencyRoundedToDollar(value: number): number {
    const n = this.parseCurrency(value);
    if (n === null) {
      return n;
    }
    return Math.round(n);
  }
}
